/* eslint-disable no-console */
// Оновлення застосунку: перевірка нової версії, банер «Оновити», скидання кешу.
// localStorage (jwt, чернетки) НЕ чистимо — лише Service Worker і Cache Storage.
import Vue from 'vue';

export const BUILD_ID = process.env.VUE_APP_BUILD_ID || 'dev';
export const BUILD_TIME = process.env.VUE_APP_BUILD_TIME || '';
export const APP_VERSION = process.env.VUE_APP_VERSION || 'dev';

const IS_PROD = process.env.NODE_ENV === 'production';
const BASE_URL = process.env.BASE_URL || '/';
const VERSION_PARAM = '_v';
const RESET_PARAM = 'reset-cache';
const RELOAD_GUARD_KEY = 'tkd-update-reload-at';
const RELOAD_GUARD_MS = 60 * 1000;
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

// стан для банера (реактивний)
export const updateState = Vue.observable({
  available: false,
  dismissed: false,
  reason: '',
});

export function markUpdateAvailable(reason) {
  if (!updateState.available) {
    console.log('[update] Доступна нова версія:', reason);
  }
  updateState.available = true;
  updateState.reason = reason;
}

// не перезавантажуємо частіше ніж раз на хвилину (захист від циклу)
function canAutoReload() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    return Date.now() - last > RELOAD_GUARD_MS;
  } catch (e) {
    return true;
  }
}

function rememberReload() {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch (e) {
    // приватний режим — ігноруємо
  }
}

export async function clearAppCaches() {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map(registration => registration.unregister()));
  }
  if (window.caches && window.caches.keys) {
    const keys = await window.caches.keys();
    await Promise.all(keys.map(key => window.caches.delete(key)));
  }
}

// Прибирає SW і кеш, перезавантажує з ?_v=… (обхід HTTP-кешу index.html)
export async function hardReload() {
  rememberReload();
  try {
    await clearAppCaches();
  } catch (e) {
    console.error('[update] Не вдалося очистити кеш', e);
  }
  const url = new URL(window.location.href);
  url.searchParams.delete(RESET_PARAM);
  url.searchParams.set(VERSION_PARAM, String(Date.now()));
  window.location.replace(url.toString());
}

// Викликати до створення роутера: ?reset-cache=1 — ручне скидання, ?_v= — прибрати з адреси
export function handleStartupParams() {
  const url = new URL(window.location.href);
  if (url.searchParams.has(RESET_PARAM)) {
    hardReload();
    return true;
  }
  if (url.searchParams.has(VERSION_PARAM)) {
    url.searchParams.delete(VERSION_PARAM);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }
  return false;
}

export async function checkForUpdate() {
  if (!IS_PROD || updateState.available) {
    return updateState.available;
  }
  try {
    const response = await fetch(`${BASE_URL}version.json?t=${Date.now()}`, {cache: 'no-store'});
    if (!response.ok) {
      return false;
    }
    const data = await response.json();
    if (data && data.buildId && String(data.buildId) !== String(BUILD_ID)) {
      markUpdateAvailable('version');
      return true;
    }
  } catch (e) {
    // офлайн або сервер недоступний — спробуємо пізніше
  }
  return false;
}

let checksStarted = false;

export function startUpdateChecks() {
  if (!IS_PROD || checksStarted) {
    return;
  }
  checksStarted = true;

  setTimeout(checkForUpdate, 10 * 1000);
  setInterval(checkForUpdate, CHECK_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForUpdate();
    }
  });
}

function isChunkLoadError(error) {
  const message = String((error && (error.message || error)) || '');
  return (error && error.name === 'ChunkLoadError')
    || /Loading (CSS )?chunk [\w-]+ failed/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message);
}

// той самий турнір (?tab=, /bracket/:id) — не перезавантажуємо посеред роботи
function sameWorkspace(toPath, fromPath) {
  const tournament = path => (path.match(/^\/user\/tournaments\/overview\/[^/]+/) || [])[0];
  return toPath === fromPath || (tournament(toPath) && tournament(toPath) === tournament(fromPath));
}

export function installRouterUpdateHooks(router) {
  // стара вкладка після деплою просить файл, якого вже нема → оновлюємось
  router.onError(error => {
    if (isChunkLoadError(error) && canAutoReload()) {
      console.warn('[update] Не вдалося завантажити частину застосунку — оновлюємо', error);
      hardReload();
    }
  });

  // нова версія застосовується тихо при переході на іншу сторінку
  router.afterEach((to, from) => {
    if (updateState.available && from.matched.length && !sameWorkspace(to.path, from.path) && canAutoReload()) {
      hardReload();
    }
  });
}
